import { useTranslation } from 'next-i18next';

// import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { useMutation, useQueryClient } from 'react-query';

import { useScrollPosition } from '@hooks/useScrollPosition';

import Empty from '@components/common/Empty';
import { LinkItem } from '@components/common/ListItem';
import NativeInFeedAd from '@components/common/NativeInFeedAd';
import { PaginationIterator } from '@components/common/Pagination';
import Post from '@components/community/Post';
import Border from '@components/layout/Border';
import Pad from '@components/layout/Pad';
import Story from '@components/stories/Story';
import FollowingUserList from '@components/user/FollowingUserList';

import { togglePostLike } from '@api/community';
import { IFollowingFeed, fetchFollowingFeeds } from '@api/feed';
import { QUERY_KEYS } from '@constants';
import { ICommunityPost } from '@model';
import { useOptimisticUpdate, usePaginationQuery } from '@utils/query';

const FollowingFeed = () => {
  const { t } = useTranslation();

  const feedQuery = usePaginationQuery(
    QUERY_KEYS.FOLLOWING.FEED,
    ({ pageParam }) => fetchFollowingFeeds(pageParam)
  );

  useScrollPosition('followingFeed');

  const queryClient = useQueryClient();
  const optimistic = useOptimisticUpdate();

  const { mutate: toggleLike } = useMutation(togglePostLike);

  const likePost = useCallback((post: ICommunityPost) => {
    toggleLike(post.id, {
      onSuccess: () => {
        optimistic.update<IFollowingFeed>(
          QUERY_KEYS.FOLLOWING.FEED,
          (item) =>
            ({
              ...item,
              object: item.object
                ? {
                    ...item.object,
                    isLiked: !(item.object as ICommunityPost).isLiked,
                  }
                : undefined,
            } as IFollowingFeed),
          { id: `community-${post.id}` }
        );
      },
    });
  }, []);

  const pollVote = useCallback((post: ICommunityPost) => {
    optimistic.update<IFollowingFeed>(
      QUERY_KEYS.FOLLOWING.FEED,
      (item) =>
        ({
          ...item,
          object: item.object
            ? { ...item.object, poll: post.poll }
            : undefined,
        } as IFollowingFeed),
      { id: `community-${post.id}` }
    );
    const feedQueries = queryClient.getQueriesData<unknown>(['communityFeed']);
    feedQueries.forEach(([key]) => {
      optimistic.update<ICommunityPost>(
        key,
        (p) => ({ ...p, poll: post.poll }),
        { id: post.id }
      );
    });
    queryClient.invalidateQueries(['communityFeed']);
    queryClient.invalidateQueries(['userPostList']);
    queryClient.invalidateQueries(QUERY_KEYS.POST.LIST);
    queryClient.invalidateQueries(QUERY_KEYS.POST.ONE(post.id));
  }, []);

  return (
    <>
      <FollowingUserList />
      <Border />
      <PaginationIterator name="following-feed" query={feedQuery} border inFeedAd={<NativeInFeedAd unitKey="feed_default_mobile" />}>
        {(item) => {
          if (!item.object) {
            return null;
          }

          if (item.objectType === 'story') {
            return (
              <LinkItem
                hideArrow
                newTab
                to={`/stories/${item.object.id}`}
                left={<Story {...item.object} />}
              />
            );
          }

          if (item.objectType === 'community') {
            return (
              <LinkItem
                to={`/feeds/posts/${item.objectId}`}
                hideArrow
                newTab
                left={
                  <Pad>
                    <Post {...item.object} clip onLike={likePost} onPollVote={pollVote} />
                  </Pad>
                }
              />
            );
          }

          return null;
        }}
      </PaginationIterator>
      <Empty query={feedQuery}>
        <p>{t('다른 채티즈를 팔로우를 하여 나만의 피드를 구성해보세요!')}</p>
      </Empty>
    </>
  );
};

export default FollowingFeed;
